import { calculateAverageScore, convertTimestampToDateTime } from "../utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Loader } from "lucide-react"
import { useState } from "react"
import { sendReminders } from "@/api/mailService"
import toast from "react-hot-toast"
import {
    GrantApplication,
    useUpdateVotingDeadline,
} from "@/api/applicationQueries"
import { UserData } from "@/api/userQueries"

interface VotingSectionProps {
    application: GrantApplication
    applicationId: string
    userRole: string
    userId: string | undefined
    trustees: UserData[]
    isTrusteesLoading: boolean
    onVote: (vote: string) => void
    isAddVotePending: boolean
}

export function VotingSection({
    application,
    applicationId,
    userRole,
    userId,
    trustees,
    isTrusteesLoading,
    onVote,
    isAddVotePending,
}: VotingSectionProps) {
    const [isReminderEmailSending, setIsReminderEmailSending] = useState(false)
    const [votingDeadline, setVotingDeadline] = useState<string>("")
    const [showDeadlinePicker, setShowDeadlinePicker] = useState(false)
    const { mutate: updateVotingDeadline } = useUpdateVotingDeadline()

    const hasUserVoted = application.votes?.some(
        (vote) => vote.trusteeId === userId
    )

    async function handleRemindTrustees() {
        if (!trustees || trustees.length === 0) {
            toast.error("No trustees found to remind")
            return
        }

        setIsReminderEmailSending(true)

        const deadlineToUse = votingDeadline || application.votingDeadline

        const dateDeadline = deadlineToUse
            ? new Date(deadlineToUse).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
              })
            : null

        try {
            const data = trustees.map((trustee) => ({
                email: trustee.email,
                orgName: application.orgName ?? "",
                requestedAmount: application.requestedAmount ?? 0,
                link: `https://app.christinamaryhendrietrust.co.uk/dashboard?viewApplication=true&applicationId=${applicationId}`,
                deadline: dateDeadline,
            }))

            await sendReminders(data)

            updateVotingDeadline(
                {
                    applicationId,
                    deadline: showDeadlinePicker
                        ? votingDeadline
                            ? new Date(votingDeadline).toISOString()
                            : null
                        : undefined,
                    isReminder: true,
                },
                {
                    onSuccess: () => {
                        toast.success("Reminders sent successfully")
                        setShowDeadlinePicker(false)
                        setVotingDeadline("")
                    },
                    onError: () => {
                        toast.error("Failed to update reminder tracking")
                    },
                }
            )
        } catch (error) {
            toast.error("Failed to send reminders")
        } finally {
            setIsReminderEmailSending(false)
        }
    }

    return (
        <div className="flex max-md:flex-col justify-between w-full gap-4">
            <div>
                <h3 className="font-bold">Voting Status</h3>
                <p className="capitalize text-sm">
                    {application.votingStatus || "Incomplete"}
                </p>
            </div>
            <div>
                <h3 className="font-bold">Votes Cast</h3>
                <p className="capitalize text-sm">
                    {application.votes?.length || 0} / {trustees?.length || 0}
                </p>
            </div>
            <div>
                <h3 className="font-bold">Average Vote</h3>
                <p className="capitalize text-sm">
                    {calculateAverageScore(application.votes || []).toFixed(2)}
                </p>
            </div>
            {userRole === "admin" && (
                <div className="flex flex-col justify-center items-start text-center w-full md:w-[400px]">
                    <h3 className="text-center font-bold">Votes Reminder</h3>
                    {application.lastReminderSent &&
                    application.lastReminderSent.seconds !== null ? (
                        <p className="text-sm text-left">
                            Last reminded:{" "}
                            {convertTimestampToDateTime({
                                seconds: application.lastReminderSent.seconds,
                                nanoseconds:
                                    application.lastReminderSent.nanoseconds ??
                                    0,
                            })}
                            {application.reminderCount &&
                                ` (${application.reminderCount} reminder${
                                    application.reminderCount > 1 ? "s" : ""
                                } sent)`}
                        </p>
                    ) : (
                        <p className="text-sm text-left mb-2">
                            Last reminded: No reminders sent yet
                        </p>
                    )}
                    <div className="flex flex-col gap-2 w-full">
                        {application.votingDeadline && !showDeadlinePicker && (
                            <p className="text-sm text-left mb-2">
                                Current voting deadline:{" "}
                                {new Date(
                                    application.votingDeadline
                                ).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                })}
                            </p>
                        )}
                        {showDeadlinePicker ? (
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 mt-4 w-full md:w-fit">
                                <Input
                                    type="date"
                                    value={votingDeadline}
                                    onChange={(e) =>
                                        setVotingDeadline(e.target.value)
                                    }
                                    min={new Date().toISOString().split("T")[0]}
                                    className="w-full md:w-[200px]"
                                />
                                <Button
                                    variant="link"
                                    size="icon"
                                    onClick={() => {
                                        setShowDeadlinePicker(false)
                                        setVotingDeadline("")
                                    }}
                                    className="w-fit p-0 h-auto font-semibold text-sm underline underline-offset-4 text-app-defaultLight"
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="link"
                                className="w-fit p-0 h-auto font-semibold text-sm underline underline-offset-4 text-app-defaultLight"
                                onClick={() => setShowDeadlinePicker(true)}
                                disabled={
                                    application.reviewStatus === "accepted" ||
                                    application.reviewStatus === "declined"
                                }
                            >
                                {application.votingDeadline
                                    ? "Set new voting deadline"
                                    : "Set voting deadline"}
                            </Button>
                        )}
                        <Button
                            onClick={handleRemindTrustees}
                            disabled={
                                isTrusteesLoading ||
                                !trustees?.length ||
                                isReminderEmailSending ||
                                (showDeadlinePicker && !votingDeadline) ||
                                application.reviewStatus === "accepted" ||
                                application.reviewStatus === "declined"
                            }
                            className="flex items-center gap-2 w-full md:w-auto"
                        >
                            {isTrusteesLoading ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Loading trustees...
                                </>
                            ) : isReminderEmailSending ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Sending reminders...
                                </>
                            ) : application.reminderCount ? (
                                `Send new voting reminder`
                            ) : (
                                "Send voting reminder"
                            )}
                        </Button>
                    </div>
                </div>
            )}
            {userRole === "trustee" && (
                <div className="w-full md:w-auto">
                    <h3 className="font-bold">Cast your vote</h3>
                    {isAddVotePending ? (
                        <div className="flex justify-start items-center gap-2">
                            <p className="text-xs">Adding Vote</p>
                            <Loader className="animate-spin" size={14} />
                        </div>
                    ) : hasUserVoted ? (
                        <p className="text-sm">You have already voted</p>
                    ) : (
                        <Select onValueChange={onVote}>
                            <SelectTrigger className="w-full md:w-[180px]">
                                <SelectValue placeholder="Vote" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">⭐️</SelectItem>
                                <SelectItem value="2">⭐️⭐️</SelectItem>
                                <SelectItem value="3">⭐️⭐️⭐️</SelectItem>
                                <SelectItem value="4">⭐️⭐️⭐️⭐️</SelectItem>
                                <SelectItem value="5">⭐️⭐️⭐️⭐️⭐️</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                </div>
            )}
        </div>
    )
}
